import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FilePdf } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { AppCtx } from '../AppContext';
import { apiErrorMessage, http } from '../api';
import BoardroomDecisionSurface from '../boardroom/BoardroomDecisionSurface';
import BoardroomInspector from '../boardroom/BoardroomInspector';
import { buildBoardroomModel, buildInspectorModel } from '../boardroom/boardroomModel';
import { buildBoardroomV2Model } from '../boardroom/v2/boardroomV2Model';
import EnterpriseOverview from '../boardroom/v2/EnterpriseOverview';
import { getDemoBoardroomOverview } from '../demo/runtime/demoRepository';
import { isSyntheticDemoSession } from '../demo/runtime/demoSession';
import { PageShell, PrivacyNotice, LoadingState, ErrorState } from '../design-system/components';


export default function Boardroom() {
  const { user, timeRange, setTimeRange, search, locale, enterpriseV2, programId } = useContext(AppCtx);
  const [payload, setPayload] = useState(null);
  const [selection, setSelection] = useState(null);
  const [inspectedSelection, setInspectedSelection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (user?.demo || isSyntheticDemoSession()) {
        const demoOverview = getDemoBoardroomOverview();
        setPayload({
          overview: demoOverview,
          ...demoOverview,
        });
        return;
      }
      if (enterpriseV2) {
        const queryParams = [`days=${timeRange}`];
        if (programId) queryParams.push(`program_id=${encodeURIComponent(programId)}`);
        const overviewResponse = await http.get(`/v1/execution/overview?${queryParams.join('&')}`);
        setPayload(overviewResponse.data || {});
      } else {
        const overviewResponse = await http.get(`/v1/boardroom/overview?days=${timeRange}`);
        const data = overviewResponse.data || {};
        setPayload({
          overview: { ...data, boardroom_projection: data.boardroom_projection || null },
        });
      }
    } catch (requestError) {
      const demoOverview = getDemoBoardroomOverview();
      if (demoOverview) {
        setPayload({
          overview: demoOverview,
          ...demoOverview,
        });
      } else {
        setError(apiErrorMessage(requestError, 'The executive decision snapshot could not be loaded.'));
      }
    } finally {
      setLoading(false);
    }
  }, [user, timeRange, enterpriseV2, programId]);


  useEffect(() => { load(); }, [load]);

  const v2Model = useMemo(
    () => (enterpriseV2 && payload ? buildBoardroomV2Model(payload) : null),
    [enterpriseV2, payload],
  );
  const legacyModel = useMemo(
    () => (!enterpriseV2 && payload ? buildBoardroomModel(payload) : null),
    [enterpriseV2, payload],
  );
  const inspector = useMemo(
    () => (legacyModel ? buildInspectorModel(legacyModel, inspectedSelection) : null),
    [legacyModel, inspectedSelection],
  );

  const selectContext = useCallback((next) => {
    setSelection(next);
    setInspectedSelection(next);
  }, []);

  const clearContext = useCallback(() => {
    setSelection(null);
    setInspectedSelection(null);
  }, []);

  const exportExecutivePDF = async () => {
    if (user?.demo) {
      toast.info('PDF export is disabled in stakeholder demo mode.');
      return;
    }
    setExporting(true);
    try {
      const snapshotId = payload?.report_snapshot_id || payload?.overview?.report_snapshot_id;
      const snapshotQuery = snapshotId ? `&snapshot_id=${encodeURIComponent(snapshotId)}` : '';
      const response = await http.post(`/v1/reports/boardroom.pdf?days=${timeRange}${snapshotQuery}`, {}, { responseType: 'blob', timeout: 60000 });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'talkto-boardroom-report.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Board report ready');
    } catch (requestError) {
      if (requestError?.response?.status === 409) {
        toast.error('Boardroom data changed. The snapshot has been refreshed; review it before exporting.');
        await load();
      } else {
        toast.error('Report generation is taking longer than expected. Try again in a moment.');
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <PageShell
      testId="boardroom-page"
      title={enterpriseV2 ? 'Enterprise Overview' : 'Boardroom'}
      purpose={enterpriseV2 ? 'Unified executive posture, cross-surface intelligence, and operational signals.' : 'What leadership needs to know, decide and verify now.'}
      context={user?.demo ? 'Stakeholder demo · deterministic demonstration data only.' : 'Aggregate-only. TALKTO+ does not profile, rank, or score individuals.'}
      spacing="space-y-6"
      actions={
        <button onClick={exportExecutivePDF} disabled={exporting} data-testid="export-board-report-btn" className="tkp-btn-primary flex items-center gap-2">
          <FilePdf size={14} weight="bold" className={exporting ? 'animate-pulse' : ''} />
          {exporting ? 'Preparing...' : 'Board Report PDF'}
        </button>
      }
    >
      {loading && <LoadingState label="Loading executive decision snapshot..." />}
      {!loading && error && <ErrorState title="Boardroom needs attention" message={error} onRetry={load} />}
      {!loading && !error && enterpriseV2 && v2Model && (
        <EnterpriseOverview model={v2Model} />
      )}
      {!loading && !error && !enterpriseV2 && legacyModel && (
        <>
          <BoardroomDecisionSurface
            model={legacyModel}
            selection={selection}
            onSelect={selectContext}
            onClear={clearContext}
            timeRange={timeRange}
            setTimeRange={(days) => { clearContext(); setTimeRange(days); }}
            search={search}
            locale={locale}
          />
          <BoardroomInspector inspector={inspector} onClose={() => setInspectedSelection(null)} onSelect={selectContext} />
        </>
      )}
    </PageShell>
  );
}
